import { Box, Button } from "@chakra-ui/react";
import { Coupon, SparkDiscount, SparkWithRegistrationSummary } from "@greenflagdate/shared";
import { useRef, useState } from "react";
import { SheetHeader } from "../../../components/SheetHeader/SheetHeader";
import { PaymentForm, PaymentFormHandle } from "../../PaymentForm/PaymentForm";
import { PolicyCheckbox } from "../../PolicyCheckbox/PolicyCheckbox";
import { SparkCouponButton } from "../../SparkCouponButton/SparkCouponButton";
import { SparkInvoice } from "../../SparkInvoice/SparkInvoice";

interface CheckoutViewProps {
  spark: SparkWithRegistrationSummary;
  coupon: Coupon | undefined;
  onCouponApplied: (coupon: Coupon) => void;
  activeDiscount: SparkDiscount | null;
  isFree: boolean;
  isEffectivelyFree: boolean;
  registerLoading: boolean;
  onRegister: () => void;
}

export const CheckoutView = ({
  spark,
  coupon,
  onCouponApplied,
  activeDiscount,
  isFree,
  isEffectivelyFree,
  registerLoading,
  onRegister,
}: CheckoutViewProps) => {
  const paymentFormRef = useRef<PaymentFormHandle>(null);
  const [policiesAccepted, setPoliciesAccepted] = useState(false);
  const [policyError, setPolicyError] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);

  const requiresPayment = !isFree && !isEffectivelyFree;
  const submitting = registerLoading || paymentLoading;

  const handleRegisterClick = () => {
    if (submitting) return;
    if (!policiesAccepted) {
      setPolicyError("You must agree to the policies before registering.");
      return;
    }
    if (requiresPayment) {
      paymentFormRef.current?.submitForm();
    } else {
      onRegister();
    }
  };

  return (
    <Box display="flex" flexDirection="column" flex="1" minH="0">
      <Box flex="1" minH="0" overflowY="auto" pb="16px">
        <SheetHeader
          title={requiresPayment ? "Confirm & pay" : "Confirm registration"}
          subtitle={
            requiresPayment
              ? "Review your order, then enter your card."
              : "You're almost in — just confirm the policies."
          }
        />
        <Box px="20px" display="flex" flexDirection="column" gap="16px">
          <Box
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="md"
            bg="bg.canvas"
            px="14px"
          >
            <SparkInvoice
              title={spark.title}
              price={spark.price}
              coupon={coupon}
              discount={activeDiscount}
            />
            {!isFree && (
              <Box textAlign="right" pb="12px">
                <SparkCouponButton
                  sparkId={spark.id}
                  onCouponApplied={onCouponApplied}
                />
              </Box>
            )}
          </Box>

          {requiresPayment && (
            <PaymentForm
              ref={paymentFormRef}
              onLoadingStatusChange={setPaymentLoading}
              onSubmit={async () => {
                if (policiesAccepted) {
                  await onRegister();
                }
              }}
            />
          )}

          <PolicyCheckbox
            checked={policiesAccepted}
            onChange={(val) => {
              setPoliciesAccepted(val);
              if (val) setPolicyError("");
            }}
            error={policyError}
          />
        </Box>
      </Box>
      <Box
        flexShrink={0}
        px="20px"
        pt="12px"
        pb="calc(12px + env(safe-area-inset-bottom))"
        bg="bg.canvas"
        borderTop="1px solid"
        borderColor="border.subtle"
      >
        <Button
          size="2xl"
          w="100%"
          loading={submitting}
          disabled={submitting}
          onClick={handleRegisterClick}
        >
          Register
        </Button>
      </Box>
    </Box>
  );
};
